"use client";
import React, { useState, useMemo } from "react";
import Link from "next/link";
import BlogImage from '@/components/blog/BlogImage';
import { LikeButton } from '@/components/blog/LikeButton';

export default function BlogListClient({ posts }: { posts: any[] }) {
  // Gather all tags from posts
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    posts.forEach(p => (p.tags || []).forEach((t: string) => tags.add(t)));
    return Array.from(tags).sort();
  }, [posts]);
  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState('All');

  const filtered = useMemo(() => {
    return posts.filter(post => {
      const matchesTag = selectedTag === 'All' || (post.tags || []).includes(selectedTag);
      const matchesSearch =
        !search ||
        (post.title || '').toLowerCase().includes(search.toLowerCase()) ||
        (post.content || '').toLowerCase().includes(search.toLowerCase());
      return matchesTag && matchesSearch;
    });
  }, [posts, search, selectedTag]);

  return (
    <div className="w-full min-h-screen py-12 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-4 text-gray-900">
            Cannabis Education Blog
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Learn about cannabis wellness, products, and responsible consumption
          </p>
        </div>
        {/* Filter/Search UI */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12 bg-white p-6 rounded-xl shadow-md">
          <div className="flex flex-wrap gap-2 justify-center md:justify-start">
            <button
              className={`px-4 py-2 rounded-full border text-sm font-semibold transition-all duration-200 ${selectedTag === 'All' ? 'bg-primary_green text-white border-primary_green shadow-md scale-105' : 'bg-white text-gray-700 border-gray-300 hover:bg-primary_green/10 hover:border-primary_green hover:scale-105'}`}
              onClick={() => setSelectedTag('All')}
            >
              All Posts
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                className={`px-4 py-2 rounded-full border text-sm font-semibold transition-all duration-200 ${selectedTag === tag ? 'bg-primary_green text-white border-primary_green shadow-md scale-105' : 'bg-white text-gray-700 border-gray-300 hover:bg-primary_green/10 hover:border-primary_green hover:scale-105'}`}
                onClick={() => setSelectedTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
          <div className="flex justify-center md:justify-end w-full md:w-auto">
            <input
              type="text"
              className="w-full md:w-80 px-5 py-3 border-2 border-gray-300 bg-white text-gray-900 rounded-full focus:outline-none focus:ring-2 focus:ring-primary_green focus:border-primary_green text-base shadow-sm transition-all duration-200 placeholder:text-gray-400"
              placeholder="Search posts..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
        {/* Blog posts grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.length === 0 ? (
            <div className="col-span-full text-center py-24">
              <h3 className="text-2xl font-bold text-gray-700 mb-2">No posts found</h3>
              <p className="text-gray-500">Try adjusting your filters or search terms</p>
            </div>
          ) : (
            filtered.map((post: any) => (
              <Link
                key={post.id}
                href={`/blog/${post.slug}`}
                className="group flex flex-col bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-primary_green hover:-translate-y-2 focus:outline-none focus:ring-4 focus:ring-primary_green/30"
              >
                {/* Image */}
                <div className="relative w-full h-64 bg-gray-100 overflow-hidden">
                  <BlogImage
                    src={post.image}
                    alt={post.title}
                    className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute left-4 top-4">
                    {post.tags && post.tags.length > 0 && (
                      <span className="bg-primary_green text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                        {post.tags[0]}
                      </span>
                    )}
                  </div>
                </div>
                {/* Content */}
                <div className="flex-1 flex flex-col p-6">
                  <h2 className="font-bold text-xl text-gray-900 leading-tight mb-3 group-hover:text-primary_green transition-colors line-clamp-2">
                    {post.title}
                  </h2>
                  <div className="flex items-center text-sm text-gray-500 mb-4 gap-2">
                    <span>{new Date(post.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    <span>•</span>
                    <span>{post.readTime || '5 min read'}</span>
                  </div>
                  <p className="text-gray-600 mb-6 line-clamp-3 leading-relaxed">
                    {(post.content || '').split('\n')[0]}
                  </p>
                  <div className="flex justify-between items-center mt-auto pt-4 border-t">
                    <span className="text-primary_green font-semibold text-sm group-hover:underline">
                      Read article →
                    </span>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <span className="font-medium">{post.likes} likes</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
